import React, { useEffect, useMemo, useState, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputAdornment from "@material-ui/core/InputAdornment";
import Chip from "@material-ui/core/Chip";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import Pagination from "@material-ui/lab/Pagination";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import WarehouseIcon from "../../assets/images/svg/warehouse-solid-full-green.svg";
import { useDispatch } from "react-redux";
import { OAuth as OAuthActions } from "../../js/actions";
import { Context } from "../../js/utils/Context";
import AddPlantDialog from "../AddPlantNew";

const ROWS_OPTIONS = [5, 10, 20];

const COLORS = {
  navyDark: "#1e40af",
  blue800: "#1e40af",
  blue700: "#1d4ed8",
  blue100: "#dbeafe",
  blue50: "#eff6ff",
  emerald700: "#047857",
  emerald50: "#ecfdf5",
  emerald200: "#a7f3d0",
  slate500: "#64748b",
  slate100: "#f1f5f9",
  slate200: "#e2e8f0",
  amber500: "#f59e0b",
  red500: "#ef4444",
  border: "#e2e8f0",
  muted: "#f1f5f9",
  mutedForeground: "#64748b",
};

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
    padding: "24px",
    backgroundColor: "#F8FAFC",
    minHeight: "calc(100vh - 127px)",
  },

  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 24,
  },

  pageHeaderLeft: {
    display: "flex",
    alignItems: "center",
  },

  pageIconBox: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: "linear-gradient(135deg, #1a56db 0%, #1e40af 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },

  headerActions: {
    display: "flex",
    alignItems: "center",
  },

  addButton: {
    textTransform: "none",
    backgroundColor: COLORS.blue700,
    color: "#fff",
    borderRadius: 8,
    padding: "6px 16px",
    "&:hover": {
      backgroundColor: COLORS.blue800,
    },
    "& .MuiButton-label": {
      fontSize: "13px",
    },
  },

  tableCard: {
    borderRadius: 16,
    overflow: "hidden",
  },

  toolbar: {
    padding: "0px 0px 8px 24px",
    borderBottom: `1px solid ${COLORS.border}`,
  },

  searchField: {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
      borderRadius: 8,
    },
    "& .MuiOutlinedInput-root": {
      height: 50,
      width: 250,
      backgroundColor: COLORS.muted,
      borderRadius: 8,
    },
  },

  searchIcon: {
    fontSize: 18,
    color: COLORS.mutedForeground,
  },

  table: {
    tableLayout: "fixed",
  },

  tableHeadRow: {
    backgroundColor: "rgba(241,245,249,0.8)",
  },

  tableHeadCell: {
    fontSize: 12,
    color: "#64748B",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    fontWeight: 600,
  },

  plantChip: {
    backgroundColor: COLORS.blue50,
    color: COLORS.blue700,
    border: `1px solid ${COLORS.blue100}`,
    fontSize: "0.85rem",
  },

  normalRow: {
    backgroundColor: "#fff",
    "& .MuiTypography-root": {
      color: "#000",
      fontSize: "0.85rem",
    },
    "&:hover": {
      backgroundColor: "rgba(241,245,249,0.5)",
    },
  },

  emptyCell: {
    padding: "48px 0",
    color: COLORS.mutedForeground,
  },

  footer: {
    padding: "12px 24px",
    borderTop: `1px solid ${COLORS.border}`,
    backgroundColor: "rgba(241,245,249,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  footerLeft: {
    display: "flex",
    alignItems: "center",
  },

  footerText: {
    color: COLORS.mutedForeground,
    marginRight: 8,
  },

  rowsSelect: {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
      borderRadius: 8,
    },
    "& .MuiSelect-select": {
      padding: "5px 25px 5px 10px",
    },
  },

  footerRight: {
    display: "flex",
    alignItems: "center",
  },

  pageText: {
    color: COLORS.mutedForeground,
    marginRight: 12,
  },
}));
const OnboardNewPlant = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [makeSelected] = useContext(Context);

  const [plants, setPlants] = useState([]);
  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(1);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const RefreshDataSet = async () => {
    await dispatch(OAuthActions.getPlants(makeSelected.uid)).then(
      function (plantDetails) {
        if (!plantDetails || !plantDetails?._embedded?.plantLists) {
          setPlants([]);
          return;
        }

        const newDataSet = plantDetails._embedded.plantLists.map(
          (item) => ({
            uid: item.uid || "",
            plantName: item.plantName || "",
            plantCode: item.plantCode || "",
            address: item.address || "",
            country: item.country?.name || "",
            state: item.state?.name || "",
            city: item.city?.name || "",
          })
        );

        setPlants(newDataSet);
      }
    );
  };

  const loadPlants = () => {
    RefreshDataSet();
  };

  useEffect(() => {
    if (makeSelected?.uid) {
      RefreshDataSet();
    }
  }, [makeSelected]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();

    return plants.filter((plant) => {
      const matchesSearch =
        (plant.plantName || "").toLowerCase().includes(q) ||
        (plant.plantCode || "").toLowerCase().includes(q) ||
        (plant.address || "").toLowerCase().includes(q) ||
        (plant.country || "").toLowerCase().includes(q) ||
        (plant.state || "").toLowerCase().includes(q) ||
        (plant.city || "").toLowerCase().includes(q);

      return matchesSearch;
    });
  }, [search, plants]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));

  const paginated = filtered.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const handleAddPlant = () => {
    setAddDialogOpen(true);
  };
  return (
    <Box className={classes.root}>
      <Box className={classes.pageHeader}>
        <Box className={classes.pageHeaderLeft}>
          <Box className={classes.pageIconBox}>
            <img src={WarehouseIcon} alt="warehouse" style={{ width: 22, height: 22, filter: 'brightness(0) invert(1)' }} />
          </Box>

          <Typography variant="h5">
            <strong style={{ fontSize: "1.4rem" }}>Manage Plants</strong>
          </Typography>
        </Box>

        <Box className={classes.headerActions}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddPlant}
            className={classes.addButton}
            style={{ fontSize: 20 }}
          >
            Add Plant
          </Button>
        </Box>
      </Box>

      <Paper variant="outlined" className={classes.tableCard}>
        <Box className={classes.toolbar}>
          <TextField
            size="small"
            variant="outlined"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search plants..."
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className={classes.searchIcon} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer>
          <Table className={classes.table}>
            <TableHead>
              <TableRow className={classes.tableHeadRow}>
                {[
                  { label: "Plant Name", width: "16%" },
                  { label: "Plant Code", width: "16%" },
                  { label: "Address", width: "22%" },
                  { label: "Country", width: "15%" },
                  { label: "State", width: "15%" },
                  { label: "City", width: "16%" },
                ].map(({ label, width }) => (
                  <TableCell
                    key={label}
                    className={classes.tableHeadCell}
                    style={{ width }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginated.map((plant, index) => (
                <TableRow key={index} className={classes.normalRow}>
                  <TableCell>
                    <Chip
                      label={plant.plantName}
                      size="small"
                      className={classes.plantChip}
                    />
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" noWrap display="block">
                      {plant.plantCode || "-"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" display="block" style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                      {plant.address || "-"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" noWrap display="block">
                      {plant.country || "-"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" noWrap display="block">
                      {plant.state || "-"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" noWrap display="block">
                      {plant.city || "-"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}

              {paginated.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    className={classes.emptyCell}
                  >
                    No plants match your search.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box className={classes.footer}>
          <Box className={classes.footerLeft}>
            <Typography variant="body2" className={classes.footerText}>
              Rows per page:
            </Typography>

            <Select
              style={{ borderRadius: 8 }}
              size="small"
              variant="outlined"
              value={rowsPerPage}
              onChange={handleRowsPerPage}
              className={classes.rowsSelect}
            >
              {ROWS_OPTIONS.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box className={classes.footerRight}>
            <Typography variant="body2" className={classes.pageText}>
              Page {page} of {totalPages}
            </Typography>

            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              shape="circular"
              size="small"
            />
          </Box>
        </Box>
      </Paper>

      <AddPlantDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdded={loadPlants}
      />
    </Box>
  );
};

export default OnboardNewPlant;
